import React, {useEffect, useRef} from 'react';
import {shallowEqual, useSelector} from 'react-redux';
import Phaser from 'phaser';
import GameScene from '../scenes/GameScene';
import store from '../redux/store';
import {selectGridConfigState} from "../redux/reducers/sharedStateReducer/selectors";
import {REGISTRY_SINGLETON} from "../scenes/Registry";
import {DESKTOP_MIN_WIDTH} from "../constants/visuals";
import {bootTrace, isBootTraceEnabled} from "../utils/debug/bootTrace";
import {guardPhaserAudioContext} from "../utils/sound/guardPhaserAudioContext";
import {createRenderStallWatchdog} from "../utils/render/createRenderStallWatchdog";
import {getFieldDpr, toBufferSize} from "../utils/visuals/fieldBuffer";

// Витрина главного меню на десктопе стоит правее колонки меню шириной 600 px
const DEMO_MENU_COLUMN_WIDTH = 600;
const getDemoSceneWidth = () => (window.innerWidth >= DESKTOP_MIN_WIDTH
  ? window.innerWidth - DEMO_MENU_COLUMN_WIDTH
  : window.innerWidth);

// sceneSize — явный размер сцены в CSS-пикселях. Нужен витрине меню на узких
// экранах: там поле стоит в потоке блоком заданных пропорций, а сцена
// вписывается в родителя с сохранением пропорций (Scale.FIT) — без явного
// размера она считалась бы от окна и легла бы узкой полосой по центру блока
const HexGrid = ({onLoadingProgress, onGameReady, onRenderStalled, isDemoMode, sceneSize}) => {
  const gameRef = useRef(null);
  // Сравнение ПО ЗНАЧЕНИЮ, а не по ссылке. Редьюсер на каждом LOAD_STATE
  // подставляет gridConfig прямо из серверного пакета — объект каждый раз
  // новый, хотя радиус, размер гекса и ориентация те же. От этой ссылки
  // зависит эффект создания игры ниже, и без проверки по значению любой
  // LOAD_STATE (переподключение, возвращение в матч из фона, догрузка
  // состояния) разбирал сцену и бутил ее заново: секунда-две пустого фона на
  // ровном месте. Поле нестандартного радиуса по-прежнему пересоздает сцену —
  // там значения действительно другие
  const gridConfig = useSelector(selectGridConfigState, shallowEqual);

  useEffect(() => {
    // Устанавливаем данные в registry перед созданием игры
    Phaser.GameObjects.GameObjectFactory.prototype.hexagon = function (x, y, radius, fillColor, fillAlpha) {
      const points = [];
      for (let i = 0; i < 6; i++) {
        const angle = (Math.PI / 3) * i - Math.PI / 6;
        points.push(new Phaser.Geom.Point(
          x + radius * Math.cos(angle),
          y + radius * Math.sin(angle)
        ));
      }
      return this.polygon(x, y, points, fillColor, fillAlpha);
    };

    // Поле матча — на все окно (.game-container в App.css — inset: 0): шапка,
    // футер и панели лежат поверх него, а занятые ими края камера учитывает
    // как отступы свободной области (см. useFieldInsets и
    // GameScene.centerOnFreeArea). Размер окна потом меняется на месте —
    // GameScene.resizeToWindow, без пересоздания сцены. Витрина главного меню
    // считается по-старому: ее контейнер свой, и она не должна меняться
    const isMatchField = !isDemoMode && !sceneSize;
    const dpr = getFieldDpr();
    const sceneWidth = toBufferSize(sceneSize
      ? sceneSize.width
      : (isMatchField ? window.innerWidth : getDemoSceneWidth()), dpr);
    const sceneHeight = toBufferSize(sceneSize ? sceneSize.height : window.innerHeight, dpr);

    bootTrace(`HexGrid mount ${sceneWidth}x${sceneHeight} dpr=${window.devicePixelRatio} demo=${isDemoMode ? 1 : 0}`);

    const config = {
      type: Phaser.WEBGL,
      parent: 'phaser-container',
      // Черный: поле теперь лежит на картинке-подложке (createGroundBackground
      // в GameScene), и по ее краям пейзаж плавно уходит именно в этот цвет.
      // Уведя камеру далеко вбок, игрок видит черноту, а не обрезанный край
      backgroundColor: '#000000',
      scene: GameScene,
      // Pre/Post FX нигде в игре не используются, а без этих флагов Phaser на
      // буте конвейеров резервирует под PreFX по ТРИ render target'а на
      // каждый шаг размера 32px до меньшей стороны сцены плюс три
      // полноэкранных, каждый с depth-stencil буфером. На планшетах с
      // retina-канвой это гигабайтный всплеск видеопамяти в один кадр —
      // именно на нем гиб GPU-процесс Safari на iPad (черная вкладка) и
      // вис Xiaomi Pad 6 («Framebuffer status: Incomplete Attachment»)
      disablePreFX: true,
      disablePostFX: true,
      input: {
        activePointers: 2 // Включаем поддержку двух указателей для pinch-to-zoom
      },
      // Поле матча — Scale.NONE: размер задаем сами (окно в буферных пикселях,
      // а при ресайзе — game.scale.resize), и FIT ничего бы не вписывал.
      // Scale.RESIZE не подходит: он меряет родителя в CSS-пикселях и ломает
      // схему с zoom 1 / DPR. Витрине нужен FIT — ее блок задан пропорциями
      scale: {
        mode: isMatchField ? Phaser.Scale.NONE : Phaser.Scale.FIT,
        autoCenter: isMatchField ? Phaser.Scale.NO_CENTER : Phaser.Scale.CENTER_BOTH,
        width: sceneWidth,
        height: sceneHeight,
        zoom: 1 / dpr
      },
      render: {
        antialias: true,
        pixelArt: false,
        roundPixels: false
      },
      // Dev-опция для автоматизации/скрытых вкладок: ?phaserForceTimers=1
      // переводит игровой цикл с requestAnimationFrame (заморожен в скрытых
      // вкладках) на setTimeout — игра бутится и играется без видимости
      ...(process.env.NODE_ENV !== 'production'
      && window.location.search.includes('phaserForceTimers=1')
        ? {fps: {forceSetTimeOut: true}}
        : {}),
    };

    gameRef.current = new Phaser.Game(config);
    // Обработчики звука Phaser обращаются к AudioContext по таймеру и могут
    // застать его уже закрытым разбором предыдущей игры — см. подробности
    guardPhaserAudioContext(gameRef.current);
    // Конструктор синхронно создает WebGL-контекст; конвейеры с их
    // render target'ами и шейдерами собираются между этой вехой и 'ready' —
    // зависание между ними указывает на GPU-аллокации/компиляцию шейдеров
    bootTrace('Phaser.Game constructed');
    if (isBootTraceEnabled()) {
      gameRef.current.events.once('ready', () => {
        const renderer = gameRef.current && gameRef.current.renderer;
        const gl = renderer ? renderer.gl : null;
        bootTrace(`game ready webgl=${renderer && renderer.type === Phaser.WEBGL ? 1 : 0}`
          + (gl ? ` max_tex=${gl.getParameter(gl.MAX_TEXTURE_SIZE)}` : ''));
      });
    }

    // Dev-хук для отладки из консоли браузера (в проде не выставляется).
    // Массив — из-за StrictMode-дублей и демо-партий меню: живую игру ищут
    // по scene.scenes среди всех созданных
    if (process.env.NODE_ENV !== 'production') {
      window.__strategikonGames = window.__strategikonGames || [];
      window.__strategikonGames.push(gameRef.current);
      window.__strategikonGame = gameRef.current;
    }

    // Сохраняем store и конфиг в registry после создания игры
    gameRef.current.registry.set('store', store);
    gameRef.current.registry.set('gridConfig', gridConfig);
    gameRef.current.registry.set('isDemoMode', Boolean(isDemoMode));
    // Поле матча подстраивается под окно на месте (GameScene.resizeToWindow) и
    // учитывает отступы HUD; витрина живет в своем блоке и не делает ни того,
    // ни другого
    gameRef.current.registry.set('isMatchField', isMatchField);
    // Компактная витрина (узкие экраны): сцена в разы меньше игрового экрана,
    // и обычный стартовый зум показал бы пяток гексов — камере нужно отъехать
    gameRef.current.registry.set('isCompactShowcase', Boolean(sceneSize));

    // Callbacks для отслеживания прогресса загрузки и готовности сцены.
    // С включенной трассировкой прогресс (0..1 от лоадера Phaser) пишется
    // вехами по четвертям — видно, на какой доле загрузки все встало
    if (onLoadingProgress) {
      let nextTraceMilestone = 0;
      const tracedLoadingProgress = (value) => {
        if (isBootTraceEnabled() && value >= nextTraceMilestone) {
          bootTrace(`preload ${Math.round(value * 100)}%`);
          nextTraceMilestone = Math.floor(value / 0.25) * 0.25 + 0.25;
        }
        onLoadingProgress(value);
      };
      gameRef.current.registry.set('onLoadingProgress', tracedLoadingProgress);
    }
    if (onGameReady) {
      const tracedGameReady = (...args) => {
        bootTrace('scene ready');
        onGameReady(...args);
      };
      gameRef.current.registry.set('onGameReady', tracedGameReady);
    }

    REGISTRY_SINGLETON.setGameScene(gameRef.current);

    return () => {
      if (gameRef.current) {
        try {
          const game = gameRef.current;
          // gl берем ДО разбора: runDestroy обнуляет renderer.gl
          const gl = game.renderer ? game.renderer.gl : null;
          // Готовность менеджера сцен, а не game.isBooted: флаг игры поднимается
          // раньше, чем созданы сцены, и разбор на полпути падает на
          // systemScene.sys, оставляя рендерер живым
          const isReadyForSyncDestroy = Boolean(game.scene && game.scene.isBooted);
          game.destroy(true);
          // destroy() только ставит pendingDestroy, а реальный разбор (и
          // освобождение текстур с framebuffer'ами) ждет следующего шага цикла —
          // а React к этому моменту уже создает следующую игру (демо-партия меню
          // → матч, двойной монтаж StrictMode). Два живых WebGL-контекста с
          // полными наборами текстур на мобильных GPU не помещаются, и у новой игры
          // не выделяется первый же framebuffer. Прогоняем шаг сами: step()
          // при pendingDestroy сразу вызывает runDestroy() и гасит игровой цикл.
          // Не успевшую загрузиться игру рвать на полпути нельзя — ей остается
          // отложенный разбор на первом шаге ее цикла
          // Явно теряем WebGL-контекст, не дожидаясь сборщика мусора: браузеры
          // считают живые контексты и держат их GPU-память до сборки — на
          // мобильных этого запаса нет
          const loseWebGlContext = () => {
            const loseContextExtension = gl && !gl.isContextLost() && gl.getExtension('WEBGL_lose_context');
            if (loseContextExtension) {
              loseContextExtension.loseContext();
              bootTrace('gl context released');
            }
          };
          if (isReadyForSyncDestroy) {
            game.step(0, 0);
            loseWebGlContext();
            bootTrace('HexGrid destroy (sync)');
          } else {
            // Терять контекст ПЕРЕД отложенным разбором нельзя: конвейеры
            // недособранной игры еще соберутся на 'texturesready', а в этом пути
            // у Phaser есть GL-вызовы без проверки isContextLost — на потерянном
            // контексте летит TypeError из getProgramParameter(null). Поэтому
            // ждем штатный runDestroy на первом шаге ее цикла и отпускаем
            // контекст на его событии 'destroy'
            game.events.once('destroy', loseWebGlContext);
            bootTrace('HexGrid destroy (pre-boot, deferred)');
          }
          console.log('GAME DESTROYED');
        } catch (e) {
          console.error('Error destroying Phaser game:', e);
        }
        gameRef.current = null;
      }
    };
  }, [gridConfig, onLoadingProgress, onGameReady, isDemoMode, sceneSize]);

  // Сторож зависшего рендера (см. createRenderStallWatchdog): следит, что
  // счетчик кадров игры двигается, пока браузер анимирует нас самих.
  // Отдельным эффектом от создания игры — чтобы смена обработчика не
  // перемонтировала сцену
  useEffect(() => {
    if (!onRenderStalled) {
      return undefined;
    }

    // Экземпляр игры берется НА КАЖДОМ тике, а не запоминается один раз.
    // Эффект создания игры перезапускается при смене gridConfig (его ссылка
    // меняется на каждом LOAD_STATE — например, при возвращении в сетевой
    // матч), и игра пересоздается, а этот эффект от такой смены не зависит.
    // Запомненный экземпляр после пересоздания оказался бы разобранным, его
    // счетчик кадров стоял бы вечно — и сторож поднимал бы ложную тревогу
    // как раз через несколько секунд после разворачивания приложения.
    // При смене экземпляра номер кадра меняется, и сторож сбрасывается сам
    const tick = createRenderStallWatchdog({
      getFrame: () => (gameRef.current ? gameRef.current.getFrame() : null),
      onStall: onRenderStalled,
    });

    let rafId = requestAnimationFrame(function step(now) {
      // Следующий кадр заказываем ДО тика: сторож не должен уметь оборвать
      // сам себя — иначе он повторил бы ту самую поломку, которую ловит
      rafId = requestAnimationFrame(step);
      tick(now);
    });

    return () => cancelAnimationFrame(rafId);
  }, [onRenderStalled]);

  return <div id="phaser-container" style={{width: '100%'}}/>;
};

export default HexGrid;
